import { useContext, useState, lazy, Suspense } from 'react';
import { Route, BrowserRouter, Switch, RouteProps, useLocation, Redirect } from 'react-router-dom';
import { isInIframe } from 'utils/iframe';
import { PostMessageContext } from 'component/postMessage/PostMessageContext';
import { Header } from 'component/partial/Header';
import { Box } from '@mui/material';
import { QueryClientProvider, QueryClient } from 'react-query';
import qs from 'query-string';
import Cookie from 'js-cookie';
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const PHPMyAdmin = lazy(
  () => import(/* webpackPrefetch: true */ 'component/partial/Redirects/PHPMyAdmin')
);
const WordpressManager = lazy(
  () => import(/* webpackPrefetch: true */ 'component/partial/Redirects/WordpressManager')
);
const Dashboard = lazy(() => import(/* webpackPrefetch: true */ 'component/view/Dashboard'));
const SiteDetails = lazy(() => import(/* webpackPrefetch: true */ 'component/view/SiteDetails'));
const CreateSite = lazy(() => import(/* webpackPrefetch: true */ 'component/view/CreateSite'));
const Sites = lazy(() => import(/* webpackPrefetch: true */ 'component/view/Sites'));
const Login = lazy(() => import(/* webpackPrefetch: true */ 'component/view/Login'));
const NotFound = lazy(() => import('component/view/NotFound'));

function AuthenticatedRoute(props: RouteProps & { hideHeader?: boolean }) {
  const { pathname, search } = useLocation();
  const isIframe = isInIframe();
  const sessionExists = isIframe
    ? window.sessionStorage.getItem('user_session')
    : Cookie.get('user_session');
  const qsParams = qs.parse(window.location.search);
  const { getSdk } = useContext(PostMessageContext);
  const sdk = getSdk();
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (typeof qsParams.token === 'string') {
    if (isIframe) {
      window.sessionStorage.setItem('user_session', qsParams.token);
    } else {
      Cookie.set('user_session', qsParams.token as string, {
        expires: 3560,
      });
    }
  }

  if (typeof qsParams.token === 'string' || sessionExists) {
    return (
      <Box
        sx={theme => ({
          [theme.breakpoints.up('md')]: {
            display: 'flex',
          },
          '& > div:first-of-type': {
            position: 'sticky',
            top: '0',
            zIndex: '1299',
            [theme.breakpoints.up('md')]: {
              position: 'static',
              display: 'flex',
              flex: '0 0 12rem',
            },
            [theme.breakpoints.up('lg')]: {
              flexBasis: '17.5rem',
            },
          },
          '& > main:first-of-type': {
            padding: '1.5rem 0',
            [theme.breakpoints.up('md')]: {
              flex: '1 1 auto',
              margin: '0 auto',
              padding: '2rem 0',
              minWidth: '0',
            },
          },
        })}
      >
        {(!sdk || sdk?.header === 'show') && !props.hideHeader && (
          <Header
            drawerOpen={drawerOpen}
            hasData
            onDrawerOpenToggle={() => setDrawerOpen(value => !value)}
          />
        )}
        <Route {...props} />
      </Box>
    );
  }

  window.location.href = `/login?retUrl=${pathname}${search}`;

  return null;
}

// eslint-disable-next-line react/no-multi-comp
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Switch>
          <Route
            path="/login"
            render={() => {
              return (
                <Suspense fallback={null}>
                  <Login />
                </Suspense>
              );
            }}
          />
          <AuthenticatedRoute
            exact
            path="/manage/:siteId/wordpress"
            render={() => (
              <Suspense fallback={null}>
                <WordpressManager />
              </Suspense>
            )}
            hideHeader={true}
          />
          <AuthenticatedRoute
            exact
            path="/manage/:siteId/phpmyadmin"
            render={() => (
              <Suspense fallback={null}>
                <PHPMyAdmin />
              </Suspense>
            )}
            hideHeader={true}
          />
          <AuthenticatedRoute
            exact
            path="/dashboard"
            render={() => (
              <Suspense fallback={null}>
                <Dashboard />
              </Suspense>
            )}
          />
          <AuthenticatedRoute
            exact
            path="/sites"
            render={() => (
              <Suspense fallback={null}>
                <Sites />
              </Suspense>
            )}
          />
          <AuthenticatedRoute
            exact
            path="/sites/create"
            render={() => (
              <Suspense fallback={null}>
                <CreateSite />
              </Suspense>
            )}
          />
          <AuthenticatedRoute
            path="/sites/:siteId/:tabName?"
            render={() => (
              <Suspense fallback={null}>
                <SiteDetails />
              </Suspense>
            )}
          />
          <Redirect to="/dashboard" />
          <Route
            path="*"
            render={() => {
              return <NotFound />;
            }}
          />
        </Switch>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
